import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { auth, notifications as notificationsApi } from '../api';
import {
  FiBell, FiMail, FiRefreshCw, FiPlus, FiTrash2, FiSend, FiCheck, FiX,
} from 'react-icons/fi';

const CHANNEL_LABELS = {
  email: 'Email',
  in_app: 'В приложении',
  bitrix24: 'Bitrix24',
};

function StatusBadge({ ok, label }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
      ok ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
    }`}>
      {ok ? <FiCheck /> : <FiX />}
      {label}
    </span>
  );
}

function NotificationSettings() {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [config, setConfig] = useState(null);
  const [rules, setRules] = useState([]);
  const [managers, setManagers] = useState([]);
  const [deliveries, setDeliveries] = useState([]);

  const [testEmail, setTestEmail] = useState('');
  const [newManager, setNewManager] = useState({ vendor: '', email: '', full_name: '' });

  const loadAll = async () => {
    setError('');
    const [cfg, rulesData, managersData, deliveriesData] = await Promise.all([
      notificationsApi.getConfig(),
      notificationsApi.getRules(),
      notificationsApi.getBrandManagers(),
      notificationsApi.getDeliveries(30),
    ]);
    setConfig(cfg);
    setRules(rulesData);
    setManagers(managersData);
    setDeliveries(deliveriesData);
  };

  useEffect(() => {
    (async () => {
      try {
        const me = await auth.getCurrentUser();
        setCurrentUser(me);
        if (me.is_admin) {
          await loadAll();
        }
      } catch (e) {
        setError(e.response?.data?.detail || 'Ошибка загрузки');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const flash = (msg) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(''), 4000);
  };

  const toggleRule = async (rule) => {
    try {
      await notificationsApi.updateRule(rule.id, { enabled: !rule.enabled });
      await loadAll();
      flash(rule.enabled ? 'Правило отключено' : 'Правило включено');
    } catch (e) {
      setError(e.response?.data?.detail || 'Ошибка сохранения');
    }
  };

  const addManager = async (e) => {
    e.preventDefault();
    if (!newManager.vendor.trim() || !newManager.email.trim()) return;
    try {
      await notificationsApi.createBrandManager(newManager);
      setNewManager({ vendor: '', email: '', full_name: '' });
      await loadAll();
      flash('Бренд-менеджер добавлен');
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка добавления');
    }
  };

  const removeManager = async (id) => {
    if (!window.confirm('Удалить запись?')) return;
    try {
      await notificationsApi.deleteBrandManager(id);
      await loadAll();
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка удаления');
    }
  };

  const sendTestEmail = async (e) => {
    e.preventDefault();
    if (!testEmail.trim()) return;
    try {
      const result = await notificationsApi.sendTestEmail(testEmail);
      flash(result.message);
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка отправки');
    }
  };

  const runExpiringDispatch = async () => {
    try {
      const result = await notificationsApi.dispatchExpiring();
      flash(`Проверено броней: ${result.processed}, отправлено: ${result.sent}`);
      await loadAll();
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка запуска');
    }
  };

  if (loading) {
    return <div className="text-center py-12 text-gray-500">Загрузка...</div>;
  }

  if (!currentUser?.is_admin) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FiBell />
            Уведомления
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Email, in-app и Bitrix24. Напоминание за 2 рабочих дня до окончания брони.
          </p>
        </div>
        <button
          type="button"
          onClick={() => loadAll().catch((e) => setError(e.message))}
          className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm bg-white hover:bg-gray-50"
        >
          <FiRefreshCw className="mr-2" />
          Обновить
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
          {typeof error === 'string' ? error : JSON.stringify(error)}
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md text-sm">
          {success}
        </div>
      )}

      {config && (
        <div className="bg-white shadow rounded-lg p-4">
          <h2 className="text-lg font-semibold mb-3">Статус каналов</h2>
          <div className="flex flex-wrap gap-2">
            <StatusBadge ok={config.enabled} label={config.enabled ? 'Уведомления включены' : 'Выключены'} />
            <StatusBadge ok={config.email_configured} label="Email (SMTP)" />
            <StatusBadge ok={config.bitrix_configured} label="Bitrix24" />
          </div>
          <dl className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-gray-600">
            <div><dt className="font-medium text-gray-800">Отправитель</dt><dd>{config.mail_from || '—'}</dd></div>
            <div><dt className="font-medium text-gray-800">Чат Bitrix</dt><dd>{config.bitrix_chat_id || '—'}</dd></div>
            <div className="sm:col-span-2"><dt className="font-medium text-gray-800">Сайт в письмах</dt><dd>{config.app_base_url}</dd></div>
          </dl>
          <p className="mt-3 text-xs text-gray-500">
            Настройки SMTP и Bitrix — в файле <code>data_import/notifications.env</code> на сервере.
          </p>
        </div>
      )}

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200">
          <h2 className="text-lg font-semibold">Правила уведомлений</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left font-medium text-gray-500">Название</th>
                <th className="px-4 py-2 text-left font-medium text-gray-500">Событие</th>
                <th className="px-4 py-2 text-left font-medium text-gray-500">Кому</th>
                <th className="px-4 py-2 text-left font-medium text-gray-500">Каналы</th>
                <th className="px-4 py-2 text-left font-medium text-gray-500">Дней</th>
                <th className="px-4 py-2 text-left font-medium text-gray-500">Вкл.</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {rules.map((rule) => (
                <tr key={rule.id} className={!rule.enabled ? 'bg-gray-50 opacity-70' : ''}>
                  <td className="px-4 py-3 font-medium text-gray-900">{rule.name}</td>
                  <td className="px-4 py-3 text-gray-600">{rule.event_type_label}</td>
                  <td className="px-4 py-3 text-gray-600">{rule.recipient_type_label}</td>
                  <td className="px-4 py-3 text-gray-600">
                    {(rule.channels || []).map((c) => CHANNEL_LABELS[c] || c).join(', ')}
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    {rule.event_type === 'booking_expiring_soon' ? rule.lead_business_days : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      onClick={() => toggleRule(rule)}
                      className={`px-2 py-1 rounded text-xs font-medium ${
                        rule.enabled ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-600'
                      }`}
                    >
                      {rule.enabled ? 'Вкл' : 'Выкл'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t border-gray-200 bg-gray-50">
          <button
            type="button"
            onClick={runExpiringDispatch}
            className="inline-flex items-center px-3 py-2 bg-primary-600 text-white rounded-md text-sm hover:bg-primary-700"
          >
            <FiSend className="mr-2" />
            Запустить проверку «за 2 рабочих дня»
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white shadow rounded-lg p-4">
          <h2 className="text-lg font-semibold mb-3">Бренд-менеджеры по вендору</h2>
          <p className="text-xs text-gray-500 mb-4">
            При новой брони на email менеджера уходит полная информация. Позже — синхронизация из Bitrix24.
          </p>
          <form onSubmit={addManager} className="space-y-2 mb-4">
            <input
              type="text"
              placeholder="Вендор (как в каталоге)"
              value={newManager.vendor}
              onChange={(e) => setNewManager({ ...newManager, vendor: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
            <input
              type="email"
              placeholder="Email менеджера"
              value={newManager.email}
              onChange={(e) => setNewManager({ ...newManager, email: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
            <input
              type="text"
              placeholder="ФИО (необязательно)"
              value={newManager.full_name}
              onChange={(e) => setNewManager({ ...newManager, full_name: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
            <button
              type="submit"
              className="inline-flex items-center px-3 py-2 bg-primary-600 text-white rounded-md text-sm hover:bg-primary-700"
            >
              <FiPlus className="mr-1" />
              Добавить
            </button>
          </form>
          {managers.length === 0 ? (
            <p className="text-sm text-gray-500">Список пуст — добавьте вендор и email.</p>
          ) : (
            <ul className="divide-y divide-gray-200 text-sm">
              {managers.map((m) => (
                <li key={m.id} className="py-2 flex justify-between items-start gap-2">
                  <div>
                    <span className="font-medium">{m.vendor}</span>
                    <span className="text-gray-500"> — {m.email}</span>
                    {m.full_name && <div className="text-gray-400 text-xs">{m.full_name}</div>}
                  </div>
                  <button
                    type="button"
                    onClick={() => removeManager(m.id)}
                    className="text-red-600 hover:text-red-800 p-1"
                    title="Удалить"
                  >
                    <FiTrash2 />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-white shadow rounded-lg p-4">
          <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
            <FiMail />
            Тест email
          </h2>
          <form onSubmit={sendTestEmail} className="flex gap-2">
            <input
              type="email"
              placeholder="email@example.com"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
            <button
              type="submit"
              className="px-3 py-2 bg-gray-800 text-white rounded-md text-sm hover:bg-gray-900"
            >
              Отправить
            </button>
          </form>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200">
          <h2 className="text-lg font-semibold">Журнал отправок</h2>
        </div>
        <div className="overflow-x-auto max-h-80 overflow-y-auto">
          <table className="min-w-full divide-y divide-gray-200 text-xs">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="px-3 py-2 text-left">Время</th>
                <th className="px-3 py-2 text-left">Событие</th>
                <th className="px-3 py-2 text-left">Канал</th>
                <th className="px-3 py-2 text-left">Получатель</th>
                <th className="px-3 py-2 text-left">Статус</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {deliveries.map((d) => (
                <tr key={d.id}>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {new Date(d.sent_at).toLocaleString('ru-RU')}
                  </td>
                  <td className="px-3 py-2">{d.event_type}</td>
                  <td className="px-3 py-2">{d.channel}</td>
                  <td className="px-3 py-2 max-w-[200px] truncate" title={d.recipient}>{d.recipient}</td>
                  <td className="px-3 py-2">
                    <span className={d.status === 'sent' ? 'text-green-700' : d.status === 'failed' ? 'text-red-700' : 'text-gray-500'}>
                      {d.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {deliveries.length === 0 && (
            <p className="px-4 py-6 text-sm text-gray-500 text-center">Пока нет отправок</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default NotificationSettings;
